import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonNote,
    IonSpinner,
    IonTextarea,
    IonTitle,
    IonToggle,
    IonToolbar,
} from '@ionic/react';
import {closeOutline} from 'ionicons/icons';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    AsteriskApiError,
    AsteriskMessage,
    AsteriskMessageField,
    AsteriskMessageRequest,
} from '../../model/AsteriskMessage';
import AsteriskMessageConfirmDialog from './AsteriskMessageConfirmDialog';

const NAME_MAX_LENGTH = 100;
const TEXT_MAX_LENGTH = 500;
const CONVERSION_WARNING = 'Door de gesproken tekst te wijzigen wordt de bestaande opname vervangen door ' +
    'automatisch gegenereerde spraak. Dit kan niet via deze pagina worden teruggedraaid.';

export interface AsteriskMessageSaveResult {
    success: boolean;
    error?: AsteriskApiError;
}

interface AsteriskMessageFormModalProps {
    isOpen: boolean;
    message?: AsteriskMessage;
    onDismiss: () => void;
    onSave: (request: AsteriskMessageRequest) => Promise<AsteriskMessageSaveResult>;
}

const emptyRequest = (): AsteriskMessageRequest => ({
    name: '',
    spokenText: '',
    displayText: '',
    enabled: true,
});

const requestFromMessage = (message?: AsteriskMessage): AsteriskMessageRequest => message ? ({
    name: message.name,
    spokenText: message.spokenText,
    displayText: message.displayText,
    enabled: message.enabled,
}) : emptyRequest();

const normalizeRequest = (request: AsteriskMessageRequest): AsteriskMessageRequest => ({
    ...request,
    name: request.name.trim(),
    spokenText: request.spokenText.trim(),
    displayText: request.displayText.trim(),
});

const validateRequest = (request: AsteriskMessageRequest) => {
    const errors: Partial<Record<AsteriskMessageField, string>> = {};
    if (!request.name) errors.name = 'Naam is verplicht.';
    if (request.name.length > NAME_MAX_LENGTH) errors.name = `Naam mag maximaal ${NAME_MAX_LENGTH} tekens bevatten.`;
    if (!request.spokenText) errors.spokenText = 'Gesproken tekst is verplicht.';
    if (request.spokenText.length > TEXT_MAX_LENGTH) {
        errors.spokenText = `Gesproken tekst mag maximaal ${TEXT_MAX_LENGTH} tekens bevatten.`;
    }
    if (!request.displayText) errors.displayText = 'Dashboardtekst is verplicht.';
    if (request.displayText.length > TEXT_MAX_LENGTH) {
        errors.displayText = `Dashboardtekst mag maximaal ${TEXT_MAX_LENGTH} tekens bevatten.`;
    }
    return errors;
};

const AsteriskMessageFormModal: React.FC<AsteriskMessageFormModalProps> = ({
    isOpen,
    message,
    onDismiss,
    onSave,
}) => {
    const [form, setForm] = useState<AsteriskMessageRequest>(emptyRequest);
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<AsteriskMessageField, string>>>({});
    const [formError, setFormError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConversionWarning, setShowConversionWarning] = useState(false);
    const submittingRef = useRef(false);

    const normalizedRequest = useMemo(() => normalizeRequest(form), [form]);
    const spokenTextChanged = message ? normalizedRequest.spokenText !== message.spokenText.trim() : true;
    const generatesSpeech = !message || spokenTextChanged;
    const needsConversionWarning = message?.sourceType === 'PRE_RECORDED' && spokenTextChanged;

    useEffect(() => {
        if (!isOpen) return;
        setForm(requestFromMessage(message));
        setFieldErrors({});
        setFormError('');
        setShowConversionWarning(false);
    }, [isOpen, message]);

    const updateField = <Field extends AsteriskMessageField>(field: Field, value: AsteriskMessageRequest[Field]) => {
        setForm((current) => ({...current, [field]: value}));
        setFieldErrors((current) => ({...current, [field]: undefined}));
        setFormError('');
    };

    const close = () => {
        if (!submittingRef.current) onDismiss();
    };

    const save = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);
        setFormError('');
        try {
            const result = await onSave(normalizedRequest);
            if (!result.success && result.error) {
                setFieldErrors(result.error.fieldErrors);
                setFormError(result.error.message);
            }
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const submit = () => {
        if (submittingRef.current) return;
        const validationErrors = validateRequest(normalizedRequest);
        setFieldErrors(validationErrors);
        setFormError('');
        if (Object.keys(validationErrors).length > 0) return;
        if (needsConversionWarning) {
            setShowConversionWarning(true);
            return;
        }
        void save();
    };

    const confirmConversion = () => {
        setShowConversionWarning(false);
        void save();
    };

    const submittingLabel = generatesSpeech ? 'Spraak wordt gegenereerd…' : 'Wijzigingen opslaan…';

    return (
        <>
            <IonModal isOpen={isOpen} canDismiss={async () => !submittingRef.current} onDidDismiss={close}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>{message ? 'Telefoonbericht bewerken' : 'Nieuw telefoonbericht'}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={close} disabled={isSubmitting} aria-label="Formulier sluiten">
                                <IonIcon icon={closeOutline}/>
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <form className="asterisk-message-form" onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }} noValidate>
                        {formError && <div className="asterisk-form-error" role="alert">{formError}</div>}
                        <IonItem className={fieldErrors.name ? 'ion-invalid' : ''}>
                            <IonLabel position="stacked">Naam</IonLabel>
                            <IonInput
                                value={form.name}
                                maxlength={NAME_MAX_LENGTH}
                                disabled={isSubmitting}
                                required
                                aria-label="Naam"
                                aria-describedby="asterisk-name-error"
                                onIonInput={(event) => updateField('name', event.detail.value ?? '')}
                            />
                            {fieldErrors.name && <IonNote id="asterisk-name-error" color="danger">{fieldErrors.name}</IonNote>}
                        </IonItem>
                        <IonItem className={fieldErrors.spokenText ? 'ion-invalid' : ''}>
                            <IonLabel position="stacked">Gesproken tekst</IonLabel>
                            <IonTextarea
                                value={form.spokenText}
                                maxlength={TEXT_MAX_LENGTH}
                                disabled={isSubmitting}
                                required
                                autoGrow
                                aria-label="Gesproken tekst"
                                aria-describedby="asterisk-spoken-help asterisk-spoken-count asterisk-spoken-error"
                                onIonInput={(event) => updateField('spokenText', event.detail.value ?? '')}
                            />
                            <div className="asterisk-field-meta">
                                <IonNote id="asterisk-spoken-help">
                                    Deze tekst wordt tijdens het telefoongesprek uitgesproken.
                                </IonNote>
                                <IonNote id="asterisk-spoken-count">{form.spokenText.length}/{TEXT_MAX_LENGTH}</IonNote>
                            </div>
                            {fieldErrors.spokenText && (
                                <IonNote id="asterisk-spoken-error" color="danger">{fieldErrors.spokenText}</IonNote>
                            )}
                        </IonItem>
                        <IonItem className={fieldErrors.displayText ? 'ion-invalid' : ''}>
                            <IonLabel position="stacked">Dashboardtekst</IonLabel>
                            <IonTextarea
                                value={form.displayText}
                                maxlength={TEXT_MAX_LENGTH}
                                disabled={isSubmitting}
                                required
                                autoGrow
                                aria-label="Dashboardtekst"
                                aria-describedby="asterisk-display-help asterisk-display-count asterisk-display-error"
                                onIonInput={(event) => updateField('displayText', event.detail.value ?? '')}
                            />
                            <div className="asterisk-field-meta">
                                <IonNote id="asterisk-display-help">
                                    Deze tekst verschijnt nadat het bericht is afgespeeld.
                                </IonNote>
                                <IonNote id="asterisk-display-count">{form.displayText.length}/{TEXT_MAX_LENGTH}</IonNote>
                            </div>
                            {fieldErrors.displayText && (
                                <IonNote id="asterisk-display-error" color="danger">{fieldErrors.displayText}</IonNote>
                            )}
                        </IonItem>
                        <IonItem lines="none">
                            <div className="asterisk-toggle-copy">
                                <IonLabel>Actief</IonLabel>
                                <IonNote>Alleen actieve berichten zitten in de willekeurige afspeelpool.</IonNote>
                            </div>
                            <IonToggle
                                slot="end"
                                checked={form.enabled}
                                disabled={isSubmitting}
                                enableOnOffLabels
                                aria-label="Telefoonbericht actief"
                                onIonChange={(event) => updateField('enabled', event.detail.checked)}
                            />
                        </IonItem>
                        <div className="asterisk-form-actions">
                            <IonButton type="button" fill="outline" onClick={close} disabled={isSubmitting}>
                                Annuleren
                            </IonButton>
                            <IonButton type="submit" disabled={isSubmitting}>
                                {isSubmitting && <IonSpinner name="crescent" aria-hidden="true"/>}
                                {isSubmitting ? submittingLabel : 'Opslaan'}
                            </IonButton>
                        </div>
                    </form>
                </IonContent>
            </IonModal>
            <AsteriskMessageConfirmDialog
                isOpen={showConversionWarning}
                title="Bestaande opname vervangen?"
                message={<p>{CONVERSION_WARNING}</p>}
                confirmLabel="Vervangen en opslaan"
                onCancel={() => setShowConversionWarning(false)}
                onConfirm={confirmConversion}
            />
        </>
    );
};

export default AsteriskMessageFormModal;
